import { useState } from 'react'
import { Link, useNavigate } from 'react-router-dom'

import logo from '../assets/reilm-logo.svg'
import signupPanel from '../assets/register/img-signup.svg'
import iconPasswordDisabled from '../assets/register/icon-password-disabled.svg'
import iconPasswordEnabled from '../assets/register/icon-password-enabled.svg'
import { ReactComponent as IconRequirements } from '../assets/register/icon-req.svg'
import { useAuth } from '../features/auth/AuthContext'

const TooltipStatusIcon = ({ valid, color }) => (
  <svg
    width="8"
    height="8"
    viewBox="0 0 8 8"
    fill="none"
    stroke={color}
    strokeLinecap="round"
  >
    {valid ? (
      <path d="M1 3.73851L3.0928 5.96206C3.21486 6.0186 3.38514 5.94402 3.42 5.86L7 2" />
    ) : (
      <path d="M1.5 1.5L6.5 6.5M6.5 1.5L1.5 6.5" />
    )}
  </svg>
)

const BackArrow = () => (
  <svg width="17" height="12" viewBox="0 0 17 12" fill="none">
    <path
      d="M6 1L1 6l5 5M1 6h15"
      stroke="#4cbc18"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const ForwardArrow = () => (
  <svg width="17" height="12" viewBox="0 0 17 12" fill="none">
    <path
      d="M11 1l5 5-5 5M16 6H1"
      stroke="#ffffff"
      strokeWidth="2"
      strokeLinecap="round"
      strokeLinejoin="round"
    />
  </svg>
)

const Signup = () => {
  const navigate = useNavigate()
  const { signup } = useAuth()

  const [email, setEmail] = useState('')
  const [password, setPassword] = useState('')
  const [emailTouched, setEmailTouched] = useState(false)
  const [passwordTouched, setPasswordTouched] = useState(false)
  const [error, setError] = useState(null)
  const [submitting, setSubmitting] = useState(false)
  const [showRequirements, setShowRequirements] = useState(false)
  const [showPassword, setShowPassword] = useState(false)

  const isEmailValid = /^[^\s@]+@[^\s@]+\.[^\s@]+$/.test(email)
  const passwordMeetsLength = password.length >= 8
  const passwordTooShort = password.length > 0 && !passwordMeetsLength

  const showEmailError = emailTouched && email.length > 0 && !isEmailValid
  const showEmailSuccess = emailTouched && isEmailValid
  const showPasswordError = passwordTouched && passwordTooShort
  const showPasswordSuccess = passwordTouched && passwordMeetsLength

  const fieldBorder = (error, success) =>
    error ? 'border-[#d85263]' : success ? 'border-[#4cbc18]' : 'border-[#cbdcc3]'

  const handleSubmit = async (e) => {
    e.preventDefault()
    setEmailTouched(true)
    setPasswordTouched(true)
    if (!isEmailValid || !passwordMeetsLength) return
    setError(null)
    setSubmitting(true)
    try {
      const session = await signup({ email, password })
      if (session?.user && session?.accessToken) {
        navigate('/pipeline', { replace: true })
      }
    } catch (err) {
      setError(err.message || 'Unable to create account')
    } finally {
      setSubmitting(false)
    }
  }

  return (
    <div className="min-h-screen bg-brand-greenSec flex">
      <aside className="hidden lg:block w-[708px] shrink-0 bg-brand-dark">
        <img
          src={signupPanel}
          alt="Start analyzing deals with clarity and confidence. Instant ARV and comps, analyze and score deals in seconds, start in seconds."
          className="w-full h-full object-cover"
        />
      </aside>

      <div className="flex-1 flex flex-col">
        <header className="h-[91px] flex items-center px-[120px]">
          <Link to="/" className="flex items-center">
            <img src={logo} alt="reilm" className="w-[148px] h-[43px]" />
          </Link>
        </header>

        <main className="flex-1 flex items-center justify-center px-6">
          <form
            onSubmit={handleSubmit}
            className="w-[416px] flex flex-col gap-8"
          >
            <div className="flex flex-col gap-4">
              <button
                type="button"
                onClick={() => navigate(-1)}
                className="h-6 w-[112px] inline-flex items-center justify-center gap-2 pr-6 rounded-full"
              >
                <BackArrow />
                <span className="text-[16px] font-semibold text-brand-green">Back</span>
              </button>

              <h1 className="text-[40px] leading-[48px] font-extrabold text-brand-dark">
                Start 7-Day Free Trial
              </h1>

              <p className="text-[16px] leading-[28px] text-[#909090]">
                No credit card required.
              </p>
            </div>

            <div className="flex flex-col gap-4">
              <div className="flex flex-col gap-2">
                <div className="flex flex-col gap-1">
                  <input
                    type="email"
                    required
                    autoComplete="email"
                    value={email}
                    onChange={(e) => setEmail(e.target.value)}
                    onBlur={() => setEmailTouched(true)}
                    placeholder="E-mail"
                    aria-invalid={showEmailError}
                    aria-describedby={showEmailError ? 'email-error' : undefined}
                    className={`w-full h-[48px] bg-white border ${fieldBorder(showEmailError, showEmailSuccess)} rounded-full px-6 text-[14px] text-brand-dark placeholder:text-[#909090] outline-none transition-colors`}
                  />
                  {showEmailError && (
                    <p
                      id="email-error"
                      className="text-[10px] leading-[16px] text-[#d85263] px-6"
                    >
                      Please enter a valid email address.
                    </p>
                  )}
                </div>

                <div className="relative w-full">
                  <input
                    type={showPassword ? 'text' : 'password'}
                    required
                    minLength={8}
                    autoComplete="new-password"
                    value={password}
                    onChange={(e) => setPassword(e.target.value)}
                    onBlur={() => setPasswordTouched(true)}
                    placeholder="Password"
                    aria-invalid={showPasswordError}
                    className={`w-full h-[48px] bg-white border ${fieldBorder(showPasswordError, showPasswordSuccess)} rounded-full pl-6 pr-20 text-[14px] text-brand-dark placeholder:text-[#909090] outline-none transition-colors`}
                  />
                  <div className="absolute right-4 top-1/2 -translate-y-1/2 flex items-center">
                    <button
                      type="button"
                      onClick={() => setShowPassword((s) => !s)}
                      aria-label={showPassword ? 'Hide password' : 'Show password'}
                      aria-pressed={showPassword}
                      className="w-6 h-6 flex items-center justify-center"
                    >
                      <img
                        src={showPassword ? iconPasswordEnabled : iconPasswordDisabled}
                        alt=""
                        className="w-6 h-6"
                      />
                    </button>
                    <div className="relative">
                      <button
                        type="button"
                        onMouseEnter={() => setShowRequirements(true)}
                        onMouseLeave={() => setShowRequirements(false)}
                        onFocus={() => setShowRequirements(true)}
                        onBlur={() => setShowRequirements(false)}
                        aria-describedby="password-requirements"
                        aria-label="Show password requirements"
                        className="w-6 h-6 flex items-center justify-center"
                      >
                        <IconRequirements
                          className="w-[3px] h-[15px] transition-colors"
                          style={{ color: showRequirements ? '#D85263' : '#B0C3A7' }}
                        />
                      </button>

                      {showRequirements && (
                        <div
                          id="password-requirements"
                          role="tooltip"
                          className="absolute bottom-full left-1/2 -translate-x-1/2 z-10 flex flex-col items-center"
                        >
                          <div className="bg-brand-dark rounded-lg p-[14px] shadow-[0_0_5px_rgba(0,0,0,0.1)]">
                            <div className="flex items-center gap-1">
                              <TooltipStatusIcon
                                valid={passwordMeetsLength}
                                color={passwordMeetsLength ? '#4cbc18' : '#d85263'}
                              />
                              <span
                                className="text-[10px] font-light leading-[12px] whitespace-nowrap"
                                style={{ color: passwordMeetsLength ? '#4cbc18' : '#d85263' }}
                              >
                                At least 8 characters in length
                              </span>
                            </div>
                          </div>
                          <div
                            className="w-0 h-0 border-l-[7px] border-r-[7px] border-t-[10px] border-l-transparent border-r-transparent"
                            style={{ borderTopColor: '#17270d' }}
                          />
                        </div>
                      )}
                    </div>
                  </div>
                </div>
              </div>

              <p className="text-[10px] leading-[16px] text-brand-dark text-center">
                By signing up, you agree to the{' '}
                <span className="font-bold">Terms</span> and{' '}
                <span className="font-bold">Privacy Policy</span> of REILM, LLC
              </p>
            </div>

            {error && (
              <p className="text-[14px] text-red-600 font-medium text-center">
                {error}
              </p>
            )}

            <div className="flex justify-center">
              <button
                type="submit"
                disabled={submitting || passwordTooShort}
                className="w-[240px] h-[56px] inline-flex items-center justify-center gap-2.5 rounded-full bg-brand-green text-white text-[16px] font-semibold disabled:opacity-60"
              >
                <span>{submitting ? 'Creating…' : 'Create Account'}</span>
                <ForwardArrow />
              </button>
            </div>

            <p className="text-center text-[12px] leading-4">
              <span className="text-[#7d8c74]">Already have an account? </span>
              <Link to="/login" className="text-brand-green">Sign in</Link>
            </p>
          </form>
        </main>
      </div>
    </div>
  )
}

export default Signup
